import {
  getBlogLanguageLinks,
  getBlogUrl,
  type BlogAlternateInput,
  type BlogPageType,
} from "./blog-url";
import {
  articleSchema,
  breadcrumbSchema,
  schemaGraph,
  supplementalBlogSchemaNodes,
} from "./seo/schema";

export interface BlogSeoPost {
  locale: string;
  pageType: BlogPageType | string;
  slug?: string;
  countryCode?: string | null;
}

export interface HeadLink {
  rel: string;
  href: string;
  hrefLang?: string;
}

export function getBlogHeadLinks(
  self: BlogSeoPost,
  alternate?: BlogSeoPost | null,
): HeadLink[] {
  const canonical = getBlogUrl(self as BlogAlternateInput);
  const languageLinks = getBlogLanguageLinks(
    self as BlogAlternateInput,
    alternate as BlogAlternateInput | null | undefined,
  );
  return [
    { rel: "canonical", href: canonical.absolute },
    ...languageLinks.map((link) => ({
      rel: "alternate",
      hrefLang: link.hrefLang,
      href: link.href,
    })),
  ];
}

export function getBlogCanonical(self: BlogSeoPost) {
  return getBlogUrl(self as BlogAlternateInput);
}

export interface BlogArticleSchemaData extends BlogSeoPost {
  slug: string;
  title: string;
  excerpt: string | null;
  publishedAt: Date | string | null;
  updatedAt: Date | string | null;
  author: string | null;
  tags: string[];
  readingTimeMin: number | null;
  ogImageUrl: string | null;
  schemaJson: Record<string, unknown> | null;
}

function toIso(value: Date | string | null): string | null {
  if (!value) return null;
  return value instanceof Date ? value.toISOString() : value;
}

export function buildBlogArticleJsonLd(post: BlogArticleSchemaData): string {
  const canonical = getBlogCanonical(post);
  const blog = getBlogCanonical({ locale: post.locale, pageType: "index" });
  const schemas: unknown[] = [
    articleSchema({
      slug: post.slug,
      title: post.title,
      description: post.excerpt ?? "",
      publishedAt: toIso(post.publishedAt) ?? new Date(0).toISOString(),
      updatedAt: toIso(post.updatedAt) ?? undefined,
      author: post.author ?? "Alchema",
      locale: post.locale,
      tags: post.tags,
      readingTimeMin: post.readingTimeMin ?? 5,
      coverImage: post.ogImageUrl ?? undefined,
      pageType: post.pageType,
      countryCode: post.countryCode,
    }),
    breadcrumbSchema([
      { name: "Home", url: post.locale === "en" ? "/" : `/${post.locale}` },
      { name: "Blog", url: blog.path },
      { name: post.title, url: canonical.path },
    ]),
    ...supplementalBlogSchemaNodes(post.schemaJson),
  ];
  return schemaGraph(...schemas);
}
